/**
 * 后台管理端接口封装。
 *
 * 对接的是 EduAim 的 .NET Core 服务（管理接口前缀 /admin/api，前台内容接口前缀 /page/api）。
 * 令牌走请求头 X-Admin-Token，存放于 localStorage，刷新页面不丢。
 *
 * 接口地址解析见 @/utils/apiBase（与前台浏览上报共用同一套规则）。
 */
import axios from 'axios';
import { API_BASE } from '@/utils/apiBase';

export { API_BASE };

const TOKEN_KEY = 'edu_admin_token';

export function getToken() {
    return window.localStorage.getItem(TOKEN_KEY) || '';
}

export function setToken(token) {
    window.localStorage.setItem(TOKEN_KEY, token || '');
}

export function clearToken() {
    window.localStorage.removeItem(TOKEN_KEY);
}

const http = axios.create({
    baseURL: API_BASE,
    timeout: 20000
});

/** 请求：带上管理令牌 */
http.interceptors.request.use(
    config => {
        const token = getToken();
        if (token) {
            config.headers['X-Admin-Token'] = token;
        }
        return config;
    },
    error => Promise.reject(error)
);

/**
 * 响应：统一按后端的 ApiResult 解包。
 *   { code: 0, message: 'ok', data: ... }  →  resolve(data)
 *   其它 code 一律 reject，401 顺带清掉本地令牌。
 */
http.interceptors.response.use(
    response => {
        const body = response.data;
        if (body && typeof body.code !== 'undefined') {
            if (body.code === 0) {
                return Promise.resolve(body.data);
            }
            if (body.code === 401) {
                clearToken();
            }
            const err = new Error(body.message || '请求失败');
            err.code = body.code;
            return Promise.reject(err);
        }
        return Promise.resolve(body);
    },
    error => {
        let message = '网络异常，请确认后端服务已启动';
        if (error && error.response) {
            const s = error.response.status;
            if (error.response.data && error.response.data.message) {
                message = error.response.data.message;
            } else if (s === 401 || s === 403) {
                message = '登录已失效，请重新登录';
                clearToken();
            } else if (s === 404) {
                message = '接口不存在（' + API_BASE + '）';
            } else if (s === 500) {
                message = '服务端错误';
            }
        } else if (error && error.message && /timeout/i.test(error.message)) {
            message = '请求超时，请稍后重试';
        }
        const err = new Error(message);
        err.raw = error;
        return Promise.reject(err);
    }
);

export default http;

/* ------------------------------ 管理接口 ------------------------------ */

export const adminApi = {
    /** 登录，成功返回 { token, expires, ... } */
    login(password) {
        return http.post('/admin/api/login', { password });
    },

    /** 概览（服务自检信息） */
    index() {
        return http.get('/admin/api');
    },

    /**
     * 页面列表
     * @param {{keyword?:string,status?:string,page?:number,size?:number,seed?:boolean}} params
     */
    pages(params) {
        return http.get('/admin/api/pages', { params: params || {} });
    },

    /** 页面详情（含诗句与正文段落），传 id 或 code */
    page(id, code) {
        return http.get('/admin/api/page', { params: { id: id || 0, code: code || '' } });
    },

    /** 可用主题 */
    themes() {
        return http.get('/admin/api/page/themes');
    },

    /** 新增 / 编辑（id 为 0 表示新增） */
    save(payload) {
        return http.post('/admin/api/page/save', payload);
    },

    /** 状态流转：0 草稿 / 1 已发布 / 2 已下线 */
    status(id, status) {
        return http.post('/admin/api/page/status', { id: id, status: status });
    },

    /** 删除 */
    remove(id) {
        return http.post('/admin/api/page/delete', { id: id });
    },

    /** 调整排序 */
    sort(id, sort) {
        return http.post('/admin/api/page/sort', { id: id, sort: sort });
    },

    /** 重新导入内置页面（已存在的 code 会跳过，不会覆盖已改内容） */
    seed() {
        return http.post('/admin/api/page/seed');
    }
};

/* ------------------------------ 前台只读接口 ------------------------------ */

export const publicApi = {
    /** 已发布页面列表（信箱列表用） */
    pages() {
        return http.get('/page/api/pages');
    },
    /** 已发布页面详情 */
    page(code) {
        return http.get('/page/api/page/' + encodeURIComponent(code));
    }
};

/* ------------------------------ 访问记录 ------------------------------ */

export const visitApi = {
    /**
     * 访问记录列表（分页 + 多条件筛选）
     * @param {{keyword?:string,account?:string,ip?:string,source?:string,device?:string,
     *          pageCode?:string,startDate?:string,endDate?:string,order?:string,
     *          page?:number,size?:number}} params
     */
    list(params) {
        return http.get('/admin/api/visits', { params: params || {} });
    },

    /** 访问概览：今日/累计 PV·UV·IP、近 7 日趋势、热门页面、活跃账号、最近访问 */
    stats() {
        return http.get('/admin/api/visit/stats');
    },

    /** 可选筛选项（出现过的账号 / IP / 页面路径） */
    facets() {
        return http.get('/admin/api/visit/facets');
    },

    /** 导出（同一套筛选条件，最多 limit 条） */
    export(params) {
        return http.get('/admin/api/visit/export', { params: params || {} });
    },

    /** 删除单条 */
    remove(id) {
        return http.post('/admin/api/visit/delete', { id: id });
    },

    /**
     * 清理
     * @param {{mode:'all'|'days'|'before',days?:number,date?:string}} payload
     */
    clear(payload) {
        return http.post('/admin/api/visit/clear', payload);
    }
};
